import { useCallback, useEffect, useRef, useState } from 'react'

type UseTimerProps = {
    start?: number
    end: number
    interval?: number
    onComplete?: () => void
    startPredicate: boolean
}

export const useCounter = ({ start = 0, end, interval = 1000, onComplete, startPredicate }: UseTimerProps) => {
    const [count, setCount] = useState(start)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const hasCompletedRef = useRef(false)

    const stopTimer = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!startPredicate || end <= start) {
            stopTimer()
            setCount(start)
            hasCompletedRef.current = false
            return
        }

        intervalRef.current = setInterval(() => {
            setCount((currentCount) => {
                if (currentCount >= end && !hasCompletedRef.current) {
                    hasCompletedRef.current = true
                    stopTimer()
                    onComplete?.()
                    return end
                }
                return currentCount + 1
            })
        }, interval)

        return stopTimer
    }, [end, interval, onComplete, start, startPredicate, stopTimer])

    return count
}
